import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckIcon from '@mui/icons-material/Check';
import { apiFetch } from '../api/api';
import { isAppServiceItem, withServiceQuery } from '../config/service';
import { selectCartItems, addOrUpdateItem, selectCartEntry } from '../store/cartSlice';
import { selectFavouriteIds, toggleFavourite } from '../store/favouritesSlice';
import { mapApiProduct, sortApiProducts, isProductActive } from '../utils/mapProduct';
import {
  formatProductOriginalPrice,
  formatProductPrice,
  hasProductDiscount,
  hasProductPrice,
} from '../utils/price';
import { buildProductConsultWhatsAppMessage, openWhatsApp } from '../utils/whatsapp';
import { useCartMessage } from '../context/CartMessageContext';

const PRODUCTS_FETCH_LIMIT = 100;

function FavouriteCard({ product, inCart, onRemove }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showCartMessage } = useCartMessage();
  const cartEntry = useSelector(selectCartEntry(product.id));
  const [justAdded, setJustAdded] = useState(false);

  const isActive = isProductActive(product);
  const hidePrice = Boolean(product.hidePrice);
  const hasDiscount = !hidePrice && hasProductDiscount(product);
  const showPrice = !hidePrice && hasProductPrice(product);

  const openProduct = () => navigate(`/product/${product.id}`, { state: { product } });

  const quickAdd = (e) => {
    e.stopPropagation();
    if (!isActive || hidePrice) return;
    dispatch(
      addOrUpdateItem({
        product,
        quantity: (cartEntry?.quantity ?? 0) + 1,
        note: cartEntry?.note ?? '',
      }),
    );
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 750);
    showCartMessage(t('added_to_cart'), 'success');
  };

  const openProductConsult = (e) => {
    e.stopPropagation();
    const message = buildProductConsultWhatsAppMessage({
      product,
      currency: t('orders_currency'),
      labels: {
        title: t('product_wa_consult_title'),
        product: t('product_wa_product'),
        price: t('product_wa_price'),
        originalPrice: t('product_wa_original_price'),
        discount: t('product_wa_discount'),
      },
    });
    openWhatsApp(message);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      onClick={openProduct}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        opacity: isActive ? 1 : 0.8,
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: 4,
          borderColor: 'primary.light',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', sm: 160 },
            minHeight: { xs: 180, sm: 160 },
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            flexShrink: 0,
          }}
        >
          {hasDiscount && product.discountPercent != null && (
            <Chip
              label={`-${product.discountPercent}%`}
              size="small"
              color="secondary"
              sx={{ position: 'absolute', top: 12, insetInlineStart: 12, fontWeight: 800 }}
            />
          )}
          <Box
            component="img"
            src={product.image}
            alt={product.name}
            sx={{
              maxWidth: '100%',
              maxHeight: 130,
              objectFit: 'contain',
              filter: isActive ? 'none' : 'grayscale(0.5) opacity(0.6)',
            }}
          />
        </Box>

        <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.4} sx={{ flex: 1 }}>
              {product.name}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={t('remove_favourite')}
              sx={{
                color: 'secondary.main',
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'secondary.light', color: '#fff' },
              }}
            >
              <FavoriteIcon fontSize="small" />
            </IconButton>
          </Box>

          {product.description && (
            <Typography variant="body2" color="text.secondary" sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {product.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', gap: 1, flexWrap: 'wrap' }}>
            {hidePrice ? (
              <Box
                sx={{
                  maxWidth: 200,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, rgba(212,165,116,0.18), rgba(184,137,74,0.12))',
                  border: '1px solid rgba(184,137,74,0.35)',
                  boxShadow: '0 2px 8px rgba(184,137,74,0.12)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: '#8F6A33',
                    lineHeight: 1.4,
                    fontSize: '0.82rem',
                  }}
                >
                  {t('price_ask_message')}
                </Typography>
              </Box>
            ) : showPrice ? (
              <Box>
                <Typography variant="h6" fontWeight={800} color="primary.main" lineHeight={1.2}>
                  {formatProductPrice(product)}
                </Typography>
                {hasDiscount && (
                  <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {formatProductOriginalPrice(product)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={1} alignItems="center" onClick={(e) => e.stopPropagation()}>
              <IconButton
                onClick={quickAdd}
                disabled={!isActive || hidePrice}
                size="small"
                sx={{
                  bgcolor: justAdded ? 'success.main' : 'primary.main',
                  color: '#fff',
                  '&:hover': { bgcolor: justAdded ? 'success.dark' : 'primary.dark' },
                  '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                }}
                aria-label={t('add_to_cart')}
              >
                {justAdded ? <CheckIcon fontSize="small" /> : <ShoppingCartOutlinedIcon fontSize="small" />}
              </IconButton>
              <IconButton
                onClick={openProductConsult}
                size="small"
                aria-label={t('product_consult_whatsapp')}
                sx={{
                  bgcolor: '#25D366',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1da851' },
                }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
              {inCart && (
                <Chip label={t('added_to_cart')} size="small" color="success" variant="outlined" />
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Favourites() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const favouriteIds = useSelector(selectFavouriteIds);
  const cartItems = useSelector(selectCartItems);

  const [favouriteProducts, setFavouriteProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (favouriteIds.length === 0) {
      setFavouriteProducts([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await apiFetch(
          withServiceQuery(`/users/products?per_page=${PRODUCTS_FETCH_LIMIT}`),
        );
        if (!response.ok) throw new Error('products_fetch_failed');

        const json = await response.json();
        const favouriteIdSet = new Set(favouriteIds);
        const items = sortApiProducts((json.data ?? []).filter(isAppServiceItem))
          .map(mapApiProduct)
          .filter((product) => favouriteIdSet.has(product.id));

        if (!cancelled) setFavouriteProducts(items);
      } catch {
        if (!cancelled) setFavouriteProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [favouriteIds, i18n.language]);

  const cartIdSet = useMemo(
    () => new Set(cartItems.map((entry) => entry.product.id)),
    [cartItems],
  );

  return (
    <Box sx={{ bgcolor: 'background.default', pb: 6, minHeight: '60vh' }}>
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 3, mb: 4 }}>
        <Container maxWidth="lg">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={600}>
              {t('Favourites')}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #ff6b9d, #c44569)',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(196,69,105,0.35)',
              }}
            >
              <FavoriteIcon />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                {t('Favourites')}
              </Typography>
              <Typography color="text.secondary">
                {loading
                  ? '...'
                  : favouriteProducts.length > 0
                    ? `${favouriteProducts.length}`
                    : t('favourites_empty')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {loading ? (
          <Stack spacing={2}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={160} sx={{ borderRadius: 3 }} />
            ))}
          </Stack>
        ) : favouriteIds.length === 0 || favouriteProducts.length === 0 ? (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              borderRadius: 4,
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                mx: 'auto',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                border: 2,
                borderColor: 'divider',
              }}
            >
              <FavoriteBorderIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t('favourites_empty')}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t('search_hint')}
            </Typography>
            <Button component={RouterLink} to="/" variant="contained" size="large" sx={{ borderRadius: 2.5 }}>
              {t('categories')}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2.5}>
            {favouriteProducts.map((product) => (
              <Grid key={product.id} size={{ xs: 12, lg: 6 }}>
                <FavouriteCard
                  product={product}
                  inCart={cartIdSet.has(product.id)}
                  onRemove={() => dispatch(toggleFavourite(product.id))}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
